import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SessionStorageValue } from '@/shared/lib/storage';
import { Button, Dialog, Switch, Text } from '@/shared/ui';
import { useAccuracyCheckMode } from '../model/useAccuracyCheckMode';
import { useAITokenErrorMode } from '../model/useAITokenErrorMode';
import { useEmptyMode } from '../model/useEmptyMode';
import { usePrefecturalGrantsOverride } from '../model/usePrefecturalGrantsOverride';
import { useTokenUsageOverride } from '../model/useTokenUsageOverride';

/**
 * UI制御ダイアログ
 * 開発環境でのUI制御設定を行うダイアログ
 */
type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type FeatureSetting = {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

type PrefecturalGrantsOverrideValue = SessionStorageValue<'debug-prefectural-grants-override'>;

const PREFECTURAL_GRANTS_OPTION_VALUES: PrefecturalGrantsOverrideValue[] = [
  'default',
  'enabled',
  'disabled',
];

// 動的キー生成禁止ルールに準拠
const usePrefecturalGrantsOptionLabels = () => {
  const { t } = useTranslation();
  return {
    default: t('debug-menu.prefectural-grants-override.option-default'),
    enabled: t('debug-menu.prefectural-grants-override.option-enabled'),
    disabled: t('debug-menu.prefectural-grants-override.option-disabled'),
  };
};

export const UIControlDialog: FC<Props> = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const { isEmptyMode, toggleEmptyMode } = useEmptyMode();
  const { isAccuracyCheckMode, toggleAccuracyCheckMode } = useAccuracyCheckMode();
  const { isAITokenErrorMode, toggleAITokenErrorMode } = useAITokenErrorMode();
  const { overridePercentage, updateOverridePercentage } = useTokenUsageOverride();
  const { overrideValue: prefecturalOverride, updateOverrideValue: updatePrefecturalOverride } =
    usePrefecturalGrantsOverride();
  const optionLabels = usePrefecturalGrantsOptionLabels();
  const [checked, setChecked] = useState<boolean>(isEmptyMode);
  const [accuracyCheckChecked, setAccuracyCheckChecked] = useState<boolean>(isAccuracyCheckMode);
  const [aiTokenErrorChecked, setAiTokenErrorChecked] = useState<boolean>(isAITokenErrorMode);
  const [prefecturalGrantsValue, setPrefecturalGrantsValue] =
    useState<PrefecturalGrantsOverrideValue>(prefecturalOverride);
  const [tokenUsageEnabled, setTokenUsageEnabled] = useState<boolean>(overridePercentage !== null);
  const [tokenUsageValue, setTokenUsageValue] = useState<number>(overridePercentage ?? 50);

  const settings: FeatureSetting[] = [
    {
      label: t('debug-menu.empty-mode.label'),
      description: t('debug-menu.empty-mode.description'),
      checked: checked,
      onChange: setChecked,
    },
    {
      label: t('debug-menu.accuracy-check-mode.label'),
      description: t('debug-menu.accuracy-check-mode.description'),
      checked: accuracyCheckChecked,
      onChange: setAccuracyCheckChecked,
    },
    {
      label: t('debug-menu.ai-token-error-mode.label'),
      description: t('debug-menu.ai-token-error-mode.description'),
      checked: aiTokenErrorChecked,
      onChange: setAiTokenErrorChecked,
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={() => {
        toggleEmptyMode(checked);
        toggleAccuracyCheckMode(accuracyCheckChecked);
        toggleAITokenErrorMode(aiTokenErrorChecked);
        updatePrefecturalOverride(prefecturalGrantsValue);
        updateOverridePercentage(tokenUsageEnabled ? tokenUsageValue : null);
        window.location.reload();
        onOpenChange(false);
      }}
      confirmLabel={t('debug-menu.apply')}
      title={t('debug-menu.ui-control')}
    >
      <div className="space-y-4">
        <div>
          <Text variant="body">{t('debug-menu.session-storage-note')}</Text>
        </div>

        <div className="space-y-4 border-t border-b py-2">
          {settings.map((setting) => (
            <div key={setting.label} className="space-y-1">
              <div className="flex items-center justify-between">
                <Text>{setting.label}</Text>
                <Switch checked={setting.checked} onCheckedChange={setting.onChange} />
              </div>
              {setting.description && <Text variant="description">{setting.description}</Text>}
            </div>
          ))}

          {/* 都道府県補助金マッチング上書き */}
          <div className="space-y-2">
            <Text>{t('debug-menu.prefectural-grants-override.label')}</Text>
            <Text variant="description">
              {t('debug-menu.prefectural-grants-override.description')}
            </Text>
            <div className="flex gap-2">
              {PREFECTURAL_GRANTS_OPTION_VALUES.map((value) => (
                <Button
                  key={value}
                  size="sm"
                  variant={prefecturalGrantsValue === value ? 'primary' : 'outline'}
                  onClick={() => setPrefecturalGrantsValue(value)}
                >
                  {optionLabels[value]}
                </Button>
              ))}
            </div>
          </div>

          {/* トークン使用率オーバーライド */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Text>{t('debug-menu.token-usage-override.label')}</Text>
              <Switch
                checked={tokenUsageEnabled}
                onCheckedChange={(v) => setTokenUsageEnabled(v)}
              />
            </div>
            <Text variant="description">{t('debug-menu.token-usage-override.description')}</Text>
            {tokenUsageEnabled && (
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={120}
                  value={tokenUsageValue}
                  onChange={(e) => setTokenUsageValue(Number(e.target.value))}
                  className="flex-1"
                />
                <Text className="w-12 text-right">{tokenUsageValue}%</Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
